import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  UserPlus, Copy, Link2, Trash2, Loader2, AlertTriangle, CheckCircle2, Send,
} from 'lucide-react';

const ROLE_LABELS = { manager: 'Менеджер', owner: 'Руководитель', accountant: 'Бухгалтер' };

function AdminFinanceAccess() {
  const [users, setUsers] = useState([]);
  const [botConfigured, setBotConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', role: 'manager' });
  const [saving, setSaving] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // { name, deep_link }
  const [confirmDelete, setConfirmDelete] = useState(null); // user

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finance/app-users`, { headers: getAuthHeaders() });
      setUsers(r.data.users || []);
      setBotConfigured(!!r.data.bot_configured);
    } catch { toast.error('Не удалось загрузить список'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success('Ссылка скопирована'); }
    catch { toast.error('Не удалось скопировать'); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error('Укажите имя'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/finance/app-users`, form, { headers: getAuthHeaders() });
      setShowCreate(false);
      setForm({ name: '', role: 'manager' });
      setLinkModal({ name: form.name, deep_link: r.data.deep_link });
      load();
    } catch { toast.error('Ошибка при создании'); }
    finally { setSaving(false); }
  };

  const relink = async (u) => {
    try {
      const r = await axios.post(`${API}/finance/app-users/${u.id}/relink`, {}, { headers: getAuthHeaders() });
      setLinkModal({ name: u.name, deep_link: r.data.deep_link });
      load();
    } catch { toast.error('Ошибка'); }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/finance/app-users/${confirmDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Доступ удалён');
      setConfirmDelete(null);
      load();
    } catch { toast.error('Ошибка'); }
  };

  return (
    <div data-testid="finance-access-page">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Send className="w-5 h-5 text-emerald-700" /> Доступ к Telegram мини-приложению</h2>
          <p className="text-sm text-gray-500 mt-0.5">Выдайте сотрудникам персональную ссылку. Она открывает финансовое мини-приложение в Telegram-боте.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" onClick={() => setShowCreate(true)} data-testid="access-add-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Добавить сотрудника
        </Button>
      </div>

      {!botConfigured && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800" data-testid="access-bot-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Бот не настроен: не заданы <b>FINANCE_BOT_TOKEN</b> / <b>FINANCE_BOT_USERNAME</b>. Ссылки будут недоступны, пока бот не подключён.</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="access-empty">Пока никто не добавлен. Нажмите «Добавить сотрудника».</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="access-list">
          {users.map(u => (
            <div key={u.id} className="bg-white border rounded-xl p-4" data-testid={`access-user-${u.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-gray-900">{u.name}</div>
                  <div className="text-xs text-gray-500">{ROLE_LABELS[u.role] || u.role}{u.telegram_username && ` · @${u.telegram_username}`}</div>
                </div>
                {u.bound
                  ? <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Привязан</Badge>
                  : <Badge variant="outline" className="text-amber-600 border-amber-300">Ожидает</Badge>}
              </div>
              <div className="flex gap-2 mt-3">
                {u.deep_link && (
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => copyLink(u.deep_link)} data-testid={`access-copy-${u.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Ссылка
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => relink(u)} data-testid={`access-relink-${u.id}`} title="Сбросить и выдать новую ссылку">
                  <Link2 className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDelete(u)} data-testid={`access-delete-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm" data-testid="access-create-modal">
          <DialogHeader><DialogTitle>Новый сотрудник</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Создание доступа к финансовому мини-приложению.</DialogDescription>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Имя</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Напр. Айгуль Финансы" data-testid="access-name-input" />
            </div>
            <div>
              <Label className="text-sm">Роль</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="access-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Менеджер</SelectItem>
                  <SelectItem value="accountant">Бухгалтер</SelectItem>
                  <SelectItem value="owner">Руководитель</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={create} disabled={saving} data-testid="access-create-confirm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Создать ссылку'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deep-link dialog */}
      <Dialog open={!!linkModal} onOpenChange={(o) => !o && setLinkModal(null)}>
        <DialogContent className="max-w-md" data-testid="access-link-modal">
          {linkModal && (
            <>
              <DialogHeader><DialogTitle>Ссылка для {linkModal.name}</DialogTitle></DialogHeader>
              <DialogDescription>Отправьте эту ссылку сотруднику в Telegram. Он откроет её один раз — бот привяжет его и даст кнопку «Открыть Финансы».</DialogDescription>
              <div className="flex gap-2 items-center bg-gray-50 border rounded-lg p-2">
                <code className="text-xs break-all flex-1" data-testid="access-link-value">{linkModal.deep_link}</code>
                <Button size="sm" variant="outline" onClick={() => copyLink(linkModal.deep_link)} data-testid="access-link-copy"><Copy className="w-4 h-4" /></Button>
              </div>
              <DialogFooter>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setLinkModal(null)}>Готово</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm" data-testid="access-delete-modal">
          <DialogHeader><DialogTitle>Удалить доступ?</DialogTitle></DialogHeader>
          <DialogDescription>{confirmDelete && `Сотрудник «${confirmDelete.name}» потеряет доступ к финансовому мини-приложению.`}</DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Отмена</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="access-delete-confirm">Удалить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminFinanceAccess;
